/**
 * CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 -- the "Last contact
 * attempt" summary, derived PURELY from the SAME Lead 360 wire the timeline
 * consumes (no second read, no second truth). NO React, no fetch, no
 * Date.now().
 *
 * A disposition event carries the typed tokens the read-model parsed from
 * the stored lead_events row; the latest such event IS the last attempt.
 * Everything here is a labeled field (the render law: FIELDS, NOT SENTENCES)
 * and PHI-free (controlled slugs / an ISO instant / the actor role from the
 * summary -- never a value, digit, or note).
 */

import type { Lead360Event, Lead360Input } from '@/dashboard/owner/lead-360-view';

/** A disposition event is any event carrying the recorded communication
 *  result token (the [outcome:] slug the read-model surfaced). */
export function isDispositionEvent(e: Lead360Event): boolean {
  return (
    typeof e.dispositionOutcomeId === 'string' &&
    e.dispositionOutcomeId.length > 0
  );
}

export interface LastContactAttempt {
  readonly atIso: string;
  readonly channel: string | null;
  readonly resultId: string;
  readonly connectId: string | null;
  readonly detailId: string | null;
  readonly nextActionId: string | null;
  readonly callbackAtIso: string | null;
  /** The honest actor line the read-model humanized into the summary. */
  readonly summary: string;
  /** This package records ONLY agent-recorded (manual) evidence. */
  readonly evidence: 'agent_recorded';
}

/**
 * The latest disposition event on the record, projected to the last-attempt
 * summary. Returns null when the lead has no recorded disposition yet
 * (the honest empty state). Deterministic (latest occurredAt wins;
 * order-independent).
 */
export function resolveLastContactAttempt(
  record: Lead360Input,
): LastContactAttempt | null {
  let best: Lead360Event | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const e of record.events ?? []) {
    if (!isDispositionEvent(e)) continue;
    const ms = Date.parse(e.occurredAt);
    if (!Number.isFinite(ms)) continue;
    if (ms > bestMs) {
      bestMs = ms;
      best = e;
    }
  }
  if (best === null) return null;
  return {
    atIso: best.occurredAt,
    channel: best.dispositionChannel ?? null,
    resultId: best.dispositionOutcomeId as string,
    connectId: best.dispositionConnectId ?? null,
    detailId: best.dispositionDetailId ?? null,
    nextActionId: best.dispositionNextActionId ?? null,
    callbackAtIso: best.dispositionCallbackAtIso ?? null,
    summary: best.summary,
    evidence: 'agent_recorded',
  };
}

/** Humanize a controlled slug for display (underscores -> spaces). A
 *  config-authored label ride the options endpoint; this is the honest
 *  fallback when only the token is known. */
export function humanizeDispositionSlug(slug: string | null): string | null {
  if (slug === null || slug.trim().length === 0) return null;
  const spaced = slug.replace(/_/g, ' ').trim();
  return spaced.length > 0 ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : null;
}
